/** Eksportene under «booking» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/booking/`. */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

/*
 * BELØPENE BÆRER DEN NAVNGITTE REGELEN `booking_amounts` (250.19): `canSeeBookingAmounts`
 * («Se priser og honorar i booking» ELLER «endre byråets jobber»), som katalogens `requires` ikke kan
 * si. Dialogen skjuler dem med den, og API-et tar dem ut med den. Definisjonene tar dem fortsatt ut
 * selv også — det samme API-et nuller i svaret.
 */

/** Kolonner som handler om prisen uten å være et beløp. De følger beløpene ut. */
const PRICE_KEYS: ReadonlySet<string> = new Set(['priceFirm', 'artistCovers']);

const col = (
  exportName: string,
  key: string,
  type: ExportColumnMeta['type'],
  defaultHidden = false,
): ExportColumnMeta => ({
  key,
  labelKey: `export.booking.${exportName}.columns.${key}`,
  type,
  ...(type === 'money' || PRICE_KEYS.has(key) ? { rule: 'booking_amounts' as const } : {}),
  ...(defaultHidden ? { defaultHidden: true } : {}),
});

/**
 * ARTISTLISTA: registeret på Booking › Artister, med samme filter og sortering
 * (`ListBookingRegisterQuery` uten side), merkede rader, og kolonnene brukeren har valgt der
 * (`tableKey: 'booking-artists'`). Nøklene er tabellens kolonne-id-er.
 */
const ARTISTS: ExportMeta = {
  id: 'booking.artists',
  domain: 'booking',
  kind: 'table',
  titleKey: 'export.booking.artists.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['performance:list'],
  guest: false,
  tableKey: 'booking-artists',
  landscape: true,
  columns: [
    col('artists', 'name', 'text'),
    col('artists', 'genre', 'text'),
    col('artists', 'artistType', 'text'),
    col('artists', 'agency', 'text'),
    col('artists', 'price', 'money', true),
    col('artists', 'total', 'money'),
    col('artists', 'status', 'status'),
    col('artists', 'askingFee', 'money', true),
    col('artists', 'partySize', 'integer', true),
    col('artists', 'basedIn', 'text', true),
    col('artists', 'agent', 'text', true),
    col('artists', 'note', 'multiline', true),
    col('artists', 'updatedAt', 'date', true),
    col('artists', 'formats', 'text', true),
    col('artists', 'travel', 'money', true),
    col('artists', 'accommodation', 'money', true),
    col('artists', 'food', 'money', true),
    col('artists', 'artistCovers', 'text', true),
    col('artists', 'agreedFee', 'money', true),
    col('artists', 'confirmedPrice', 'money', true),
    col('artists', 'priceFirm', 'boolean', true),
    col('artists', 'latestBid', 'text', true),
    col('artists', 'latestBidAmount', 'money', true),
    col('artists', 'bookingContact', 'text', true),
    col('artists', 'contactEmail', 'email', true),
    col('artists', 'contactPhone', 'phone', true),
    col('artists', 'gender', 'status', true),
    col('artists', 'frontPerson', 'text', true),
    col('artists', 'ageGroup', 'text', true),
    col('artists', 'spotify', 'text', true),
    col('artists', 'instagram', 'text', true),
    col('artists', 'instagramFollowers', 'integer', true),
    col('artists', 'monthlyListeners', 'integer', true),
    col('artists', 'assignee', 'text', true),
    col('artists', 'identity', 'status', true),
    col('artists', 'origin', 'status', true),
    col('artists', 'shortlisted', 'boolean', true),
    col('artists', 'createdAt', 'date', true),
  ],
};

/**
 * ARTISTKORTET SOM DOKUMENT: fanene på kortet — stamdata, byrå og agent, bekreftede priser,
 * plasseringer, bud og byråhistorikk. Fra kortet (`object`) og for merkede i registeret
 * (`selection`). Prisdelen er bare med for den som ser beløp (se toppen av fila).
 */
const ARTIST_CARD: ExportMeta = {
  id: 'booking.artist-card',
  domain: 'booking',
  kind: 'document',
  titleKey: 'export.booking.artistCard.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection'],
  requires: ['performance:list'],
  guest: false,
  sections: [
    { key: 'overview', labelKey: 'export.booking.artistCard.sections.overview' },
    { key: 'agency', labelKey: 'export.booking.artistCard.sections.agency' },
    {
      key: 'price',
      labelKey: 'export.booking.artistCard.sections.price',
      rule: 'booking_amounts',
    },
    { key: 'placements', labelKey: 'export.booking.artistCard.sections.placements' },
    { key: 'bids', labelKey: 'export.booking.artistCard.sections.bids' },
    { key: 'history', labelKey: 'export.booking.artistCard.sections.history' },
  ],
};

/**
 * BOOKINGSTATUS: registerets status fra «Ikke vurdert» til «Bekreftet», gruppert på status og med
 * antall per status. For registeret med artistlistas filter (`filter`/`selection`), og for
 * årgangen (`parent`): artistene som står på årgangens programmeringsbrett.
 */
const STATUS: ExportMeta = {
  id: 'booking.status',
  domain: 'booking',
  kind: 'table',
  titleKey: 'export.booking.status.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter', 'parent'],
  requires: ['performance:list'],
  guest: false,
  columns: [
    col('status', 'status', 'status'),
    col('status', 'name', 'text'),
    col('status', 'agency', 'text'),
    col('status', 'genre', 'text'),
    col('status', 'latestBid', 'text'),
    col('status', 'respondsBy', 'date'),
    col('status', 'total', 'money'),
    col('status', 'shortlisted', 'boolean', true),
    col('status', 'bookingContact', 'text', true),
    col('status', 'contactEmail', 'email', true),
    col('status', 'contactPhone', 'phone', true),
    col('status', 'updatedAt', 'date', true),
  ],
};

/**
 * OVERGANGER: byråbyttene på fanen «Overganger», med fanens filter og sortering
 * (`ListAgencyMovesQuery` uten side) og kolonnene brukeren har valgt der, gruppert per måned.
 */
const AGENCY_MOVES: ExportMeta = {
  id: 'booking.agency-moves',
  domain: 'booking',
  kind: 'table',
  titleKey: 'export.booking.agencyMoves.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['agency_move:list'],
  guest: false,
  tableKey: 'booking-agency-moves',
  landscape: true,
  columns: [
    col('agencyMoves', 'month', 'text'),
    col('agencyMoves', 'artist', 'text'),
    col('agencyMoves', 'kind', 'status'),
    col('agencyMoves', 'fromAgency', 'text'),
    col('agencyMoves', 'toAgency', 'text'),
    col('agencyMoves', 'genre', 'text'),
    col('agencyMoves', 'base', 'text'),
    col('agencyMoves', 'source', 'status'),
    col('agencyMoves', 'evidence', 'text'),
    col('agencyMoves', 'shortlisted', 'boolean', true),
    col('agencyMoves', 'scanName', 'text', true),
  ],
};

/**
 * DIALOGEN (utover EKSPORT.md §9): bud og forespørsler på tvers av artister, fra fanen «Dialog»
 * med fanens filter (`ListBidDialogueQuery` uten side) og kolonnene brukeren har valgt der.
 */
const DIALOGUE: ExportMeta = {
  id: 'booking.dialogue',
  domain: 'booking',
  kind: 'table',
  titleKey: 'export.booking.dialogue.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['performance:list'],
  guest: false,
  tableKey: 'booking-dialogue',
  landscape: true,
  columns: [
    col('dialogue', 'artist', 'text'),
    col('dialogue', 'agency', 'text'),
    col('dialogue', 'kind', 'status'),
    col('dialogue', 'message', 'multiline'),
    col('dialogue', 'sentBy', 'text'),
    col('dialogue', 'delivery', 'text'),
    col('dialogue', 'availability', 'status'),
    col('dialogue', 'asking', 'money'),
    col('dialogue', 'amount', 'money'),
    col('dialogue', 'dealType', 'status', true),
    col('dialogue', 'respondsBy', 'date'),
    col('dialogue', 'status', 'status'),
    col('dialogue', 'performanceDate', 'date', true),
    col('dialogue', 'edition', 'text', true),
    col('dialogue', 'stage', 'text', true),
    col('dialogue', 'sentAt', 'date', true),
    col('dialogue', 'answeredAt', 'date', true),
    col('dialogue', 'responseNote', 'multiline', true),
    col('dialogue', 'notes', 'multiline', true),
    col('dialogue', 'createdAt', 'date', true),
  ],
};

/**
 * PROGRAMMERINGSBRETTET (utover EKSPORT.md §9): ett utkast på fanen «Programmering» — dag,
 * scene, tid, artist og status — gruppert per dag. Utkastet er objektet.
 */
const BOARD: ExportMeta = {
  id: 'booking.board',
  domain: 'booking',
  kind: 'table',
  titleKey: 'export.booking.board.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object'],
  requires: ['performance:list'],
  guest: false,
  columns: [
    col('board', 'day', 'date'),
    col('board', 'stage', 'text'),
    col('board', 'startsAt', 'time'),
    col('board', 'endsAt', 'time'),
    col('board', 'artist', 'text'),
    col('board', 'agency', 'text'),
    col('board', 'status', 'status'),
    col('board', 'total', 'money'),
    col('board', 'promoted', 'boolean', true),
    col('board', 'conflicts', 'text', true),
  ],
};

export const BOOKING_EXPORTS: readonly ExportMeta[] = [
  ARTISTS,
  ARTIST_CARD,
  STATUS,
  AGENCY_MOVES,
  DIALOGUE,
  BOARD,
];
